/**
 * Discount policy evaluation — single source of truth for how
 * `event_discount_policies` rows turn into a dollar amount.
 *
 * Rules (mirrored in supabase/functions/register-team-cart/index.ts):
 *  - discount_type "percentage" / "percent" → that percent of the qualifying
 *    ENTRY FEES (never scheduling requests, tier fees, or extras).
 *  - discount_type anything else ("fixed", "amount", "flat") → a dollar amount.
 *      · fixed_scope "per_team"  → value × number of qualifying teams
 *      · fixed_scope "per_order" → value once for the whole order (default)
 *  - The result is always capped at the qualifying entry fees.
 */

export type DiscountPolicyRow = {
  id?: string;
  name?: string | null;
  discount_type?: string | null;
  discount_value?: number | string | null;
  /** "per_order" (default) or "per_team" — only meaningful for fixed amounts. */
  fixed_scope?: string | null;
  condition_type?: string | null;
  condition_value?: number | string | null;
  min_registrations?: number | string | null;
};

export const isPercentPolicy = (p: DiscountPolicyRow) =>
  p.discount_type === "percent" || p.discount_type === "percentage";

export const isPerTeamFixed = (p: DiscountPolicyRow) =>
  !isPercentPolicy(p) && (p.fixed_scope ?? "per_order") === "per_team";

/** Minimum team count required, or null when the policy has no explicit condition. */
export const policyMinTeams = (p: DiscountPolicyRow): number | null => {
  const raw =
    p.condition_type === "min_teams"
      ? Number(p.condition_value ?? 0)
      : p.min_registrations != null
      ? Number(p.min_registrations)
      : NaN;
  return Number.isFinite(raw) && raw > 0 ? raw : null;
};

/**
 * Dollar amount this policy takes off, given qualifying teams and entry fees.
 * Pass `perTeamEntryFees` so a per-team fixed amount is capped at each team's
 * own entry fee rather than only at the order total.
 */
export function computePolicyDiscount(
  p: DiscountPolicyRow,
  teamCount: number,
  entryFees: number,
  perTeamEntryFees?: number[]
): number {
  const value = Number(p.discount_value ?? 0);
  if (!Number.isFinite(value) || value <= 0 || entryFees <= 0 || teamCount <= 0) {
    return 0;
  }
  const raw = isPercentPolicy(p)
    ? entryFees * (value / 100)
    : isPerTeamFixed(p)
    ? perTeamEntryFees?.length
      ? perTeamEntryFees.reduce((s, fee) => s + Math.min(value, Math.max(0, fee)), 0)
      : value * teamCount
    : value;
  return Number(Math.min(raw, entryFees).toFixed(2));
}

/**
 * Per-team allocation of a policy's discount, aligned with `perTeamEntryFees`.
 * Per-team fixed amounts land directly on each team (capped at that team's
 * entry fee); everything else spreads proportionally.
 */
export function allocatePolicyDiscount(
  p: DiscountPolicyRow,
  perTeamEntryFees: number[]
): number[] {
  const entryFees = perTeamEntryFees.reduce((s, f) => s + Math.max(0, f), 0);
  const total = computePolicyDiscount(
    p,
    perTeamEntryFees.length,
    entryFees,
    perTeamEntryFees
  );
  if (total <= 0) return perTeamEntryFees.map(() => 0);
  if (isPerTeamFixed(p)) {
    const value = Number(p.discount_value ?? 0);
    return perTeamEntryFees.map((fee) =>
      Number(Math.min(value, Math.max(0, fee)).toFixed(2))
    );
  }
  return perTeamEntryFees.map((fee) =>
    Number(((total * Math.max(0, fee)) / entryFees).toFixed(2))
  );
}

/** Human label shown in the cart, review step, and confirmation email. */
export function policyDiscountLabel(
  p: DiscountPolicyRow,
  teamCount?: number
): string {
  const value = Number(p.discount_value ?? 0);
  const name = p.name ?? "Discount";
  if (isPercentPolicy(p)) return `${name} (${value}% off entry fees)`;
  const money = `$${value.toFixed(2)}`;
  if (isPerTeamFixed(p)) {
    const suffix =
      teamCount && teamCount > 0
        ? ` (${teamCount} team${teamCount > 1 ? "s" : ""})`
        : "";
    return `${name} — ${money} off each team's entry fee${suffix}`;
  }
  return `${name} (${money} off entry fees for the order)`;
}

/** Picks the single best-saving applicable policy. */
export function evaluateDiscountPolicies(
  policies: DiscountPolicyRow[],
  teamCount: number,
  entryFees: number,
  perTeamEntryFees?: number[]
): {
  policy: DiscountPolicyRow;
  amount: number;
  label: string;
  /** Per-team allocation aligned with `perTeamEntryFees`. */
  perTeam: number[];
} | null {
  let best: DiscountPolicyRow | null = null;
  let bestAmount = 0;
  for (const p of policies ?? []) {
    const minTeams = policyMinTeams(p);
    if (minTeams == null || teamCount < minTeams) continue;
    const amount = computePolicyDiscount(p, teamCount, entryFees, perTeamEntryFees);
    if (amount > bestAmount) {
      bestAmount = amount;
      best = p;
    }
  }
  if (!best || bestAmount <= 0) return null;
  const fees = perTeamEntryFees ?? [];
  return {
    policy: best,
    amount: bestAmount,
    label: policyDiscountLabel(best, teamCount),
    perTeam: fees.length ? allocatePolicyDiscount(best, fees) : [],
  };
}

